import React, { useState } from 'react';
import './Events.css';

interface EventDate {
  month: string;
  day: string;
}

interface UpcomingEvent {
  id: number;
  featured: boolean;
  type: string;
  title: string;
  date: EventDate;
  time: string;
  location: string;
  description: string;
  info: string;
  link: string;
}

interface PastEvent {
  id: number;
  title: string;
  date: string;
  description: string;
  images?: string[];
  link?: string;
}

interface FinancialArticle {
  id: number;
  title: string;
  date: string;
  excerpt: string;
  image: string;
  source: string;
  link: string;
}

const Events: React.FC = () => {
  const [activeTab, setActiveTab] = useState('events');
  const [activeFilter, setActiveFilter] = useState('All Events');
  
  const upcomingEvents: UpcomingEvent[] = [
    {
      id: 1,
      featured: true,
      type: 'CONFERENCE',
      title: 'Northwestern Mutual Conference',
      date: { month: 'APR', day: '18' },
      time: '1:30 PM',
      location: 'Northwestern Mutual',
      description: 'Join us for an insightful conference with Northwestern Mutual to learn about career opportunities and industry insights.',
      info: 'Registration details to be announced',
      link: '#'
    }
  ];
  
  // Only keeping the Federal Reserve Bank visit in past events
  const pastEvents: PastEvent[] = [
    {
      id: 1,
      title: 'Federal Reserve Bank of New York Visit',
      date: 'MAR 28, 2025',
      description: 'Members learned about the history of the NY Fed, its current role and responsibilities, as well as monetary policy. Presentation was given by Heather Daly, Head of Economic Education at the Federal Reserve Bank of New York.',
      images: ['/assets/team.png'],
      link: 'https://www.newyorkfed.org/aboutthefed/visiting.html'
    }
  ];
  
  // Adding financial news articles
  const financialArticles: FinancialArticle[] = [
    {
      id: 1,
      title: "Fed Keeps Interest Rates Steady Amid Inflation Concerns",
      date: "March 25, 2025",
      excerpt: "The Federal Reserve maintained its benchmark interest rate while signaling potential future adjustments in response to persistent inflation pressures. Chair Jerome Powell emphasized a data-dependent approach moving forward.",
      image: "https://via.placeholder.com/600x400",
      source: "Federal Reserve",
      link: "https://www.federalreserve.gov/newsevents/pressreleases/monetary20240320a.htm"
    },
    {
      id: 2,
      title: "SEC Proposes New Rules for Cryptocurrency Exchanges",
      date: "March 20, 2025",
      excerpt: "The Securities and Exchange Commission unveiled new regulatory framework aimed at bringing cryptocurrency trading platforms under traditional financial oversight. The proposal includes enhanced disclosures and investor protections.",
      image: "https://via.placeholder.com/600x400",
      source: "SEC",
      link: "https://www.sec.gov/news/speeches-statements"
    },
    {
      id: 3,
      title: "Global Banking Stress Test Results Reveal Sector Strength",
      date: "March 10, 2025",
      excerpt: "International regulators published findings showing major financial institutions have improved capital positions, though some regional vulnerabilities persist. The comprehensive assessment evaluated responses to hypothetical market shocks.",
      image: "https://via.placeholder.com/600x400",
      source: "Bank for International Settlements",
      link: "https://www.bis.org/bcbs/publ/d554.htm"
    },
    {
      id: 4,
      title: "AI Revolution in Financial Services: What Students Need to Know",
      date: "March 5, 2025",
      excerpt: "How artificial intelligence is transforming investment analysis, risk management, and career opportunities for finance graduates. Industry leaders discuss the evolving skillset required for success in tomorrow's financial landscape.",
      image: "https://via.placeholder.com/600x400",
      source: "McKinsey & Company",
      link: "https://www.mckinsey.com/industries/financial-services/our-insights"
    },
    {
      id: 5,
      title: "ESG Investing: Performance Analysis and Future Trends",
      date: "February 28, 2025",
      excerpt: "Recent data shows how environmental, social, and governance-focused investments are performing compared to traditional portfolios. The report highlights sector-specific impacts and regulatory developments affecting sustainable investing.",
      image: "https://via.placeholder.com/600x400",
      source: "Morningstar",
      link: "https://www.morningstar.com/sustainable-investing"
    },
    {
      id: 6,
      title: "Digital Dollar Pilot Program Expands to Additional Financial Institutions",
      date: "February 20, 2025",
      excerpt: "The Federal Reserve has announced the expansion of its digital currency pilot program to include twelve more financial institutions. This phase will test cross-border transactions and integration with existing payment systems.",
      image: "https://via.placeholder.com/600x400",
      source: "Federal Reserve",
      link: "https://www.federalreserve.gov/cbdc-research.htm"
    }
  ];
  
  const filteredEvents = activeFilter === 'All Events' 
    ? upcomingEvents 
    : upcomingEvents.filter(event => event.type === activeFilter.toUpperCase());
  
  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter);
  };
  
  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };
  
  return (
    <section className="events">
      <div className="events-hero">
        <div className="events-hero-content">
          <h1>Adelphi University Finance Club Insights</h1>
          <p>Stay connected with club events and the latest developments in the financial world</p>
        </div>
      </div>
      
      <div className="events-container">
        <div className="events-tabs">
          <button 
            className={activeTab === 'events' ? 'active' : ''}
            onClick={() => handleTabClick('events')}
          >
            Club Events
          </button>
          <button 
            className={activeTab === 'articles' ? 'active' : ''}
            onClick={() => handleTabClick('articles')}
          >
            Financial Insights
          </button>
        </div>
        
        {activeTab === 'events' ? (
          <>
            <div className="events-filter">
              {['All Events', 'NETWORKING', 'WORKSHOP', 'CONFERENCE'].map((filter) => (
                <button 
                  key={filter}
                  className={activeFilter === filter ? 'active' : ''}
                  onClick={() => handleFilterClick(filter)}
                >
                  {filter === 'NETWORKING' ? 'Networking' : 
                   filter === 'WORKSHOP' ? 'Workshops' : 
                   filter === 'CONFERENCE' ? 'Conferences' : filter}
                </button>
              ))}
            </div>
            
            <div className="events-grid">
              {filteredEvents.length > 0 ? (
                filteredEvents.map((event) => (
                  <div key={event.id} className={`event-card ${event.featured ? 'featured' : ''}`}>
                    {event.featured && <div className="event-card-badge">Featured</div>}
                    <div className="event-card-date">
                      <div className="date-month">{event.date.month}</div>
                      <div className="date-day">{event.date.day}</div>
                    </div>
                    <div className="event-card-content">
                      <span className="event-type">{event.type}</span>
                      <h3>{event.title}</h3>
                      <div className="event-details">
                        <span><i className="event-icon time-icon"></i>{event.time}</span>
                        <span><i className="event-icon location-icon"></i>{event.location}</span>
                      </div>
                      <p>{event.description}</p>
                      <div className="event-footer">
                        <span className="event-info">{event.info}</span>
                        <a href={event.link} target="_blank" rel="noopener noreferrer" className="event-link">Learn More</a>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-events-message">
                  <h3>No events found for this category</h3>
                  <p>Please check back later or select a different category.</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="financial-articles-grid">
            {financialArticles.map((article) => (
              <div key={article.id} className="article-card">
                <div className="article-image">
                  <img src={article.image} alt={article.title} />
                  <div className="article-source">{article.source}</div>
                </div>
                <div className="article-content">
                  <div className="article-date">{article.date}</div>
                  <h3 className="article-title">{article.title}</h3>
                  <p className="article-excerpt">{article.excerpt}</p>
                  <a href={article.link} target="_blank" rel="noopener noreferrer" className="article-link">
                    Read Full Article <span className="arrow-icon">→</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="past-events-section">
        <div className="section-header">
          <h2>Past Events</h2>
          <p className="section-subtitle">Revisit our previous successful events</p>
        </div>
        
        <div className="past-grid">
          {pastEvents.map((event) => (
            <div key={event.id} className="past-event-card">
              <div className="event-image">
                {event.images ? (
                  <div className="event-image-gallery">
                    {event.images.map((img, index) => (
                      <img key={index} src={img} alt={`${event.title} image ${index + 1}`} />
                    ))}
                  </div>
                ) : (
                  <div className="event-image-placeholder"></div>
                )}
                <div className="event-overlay">
                  <div className="overlay-content">
                    <span>{event.date}</span>
                    <h4>{event.title}</h4>
                    {event.link && (
                      <a href={event.link} target="_blank" rel="noopener noreferrer" className="attendance-button">Learn More</a>
                    )}
                  </div>
                </div>
              </div>
              <div className="past-event-content">
                <h4>{event.title}</h4>
                <p>{event.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Events;